import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getFounding100Remaining } from "../lib/api";
import { CURRENCIES, type Currency, loadCurrency } from "../lib/currency";
import { useLang, useLp } from "../lib/i18n";
import { sourcePageFromPath, trackGoal } from "../lib/plausible";

const copy = {
  en: {
    label: "Founding 100",
    text: (n: number) => `Only ${n} Founding 100 spots left`,
    note: (price: string) => `${price}/mo equivalent for 12 months`,
    cta: "Claim a spot",
  },
  pt: {
    label: "Founding 100",
    text: (n: number) => `Restam ${n} vagas Founding 100`,
    note: (price: string) => `${price}/mês por 12 meses`,
    cta: "Garantir vaga",
  },
  es: {
    label: "Founding 100",
    text: (n: number) => `Quedan ${n} cupos Founding 100`,
    note: (price: string) => `Equivalente a ${price}/mes por 12 meses`,
    cta: "Reservar cupo",
  },
} as const;

const hiddenPathFragments = [
  "/account",
  "/auth/callback",
  "/billing/",
];

export function Founding100Banner() {
  const lang = useLang();
  const lp = useLp();
  const location = useLocation();
  const [remaining, setRemaining] = useState<number | null>(null);
  const [currency, setCurrency] = useState<Currency>(() => loadCurrency());

  useEffect(() => {
    let cancelled = false;
    getFounding100Remaining()
      .then(({ remaining: value }) => {
        if (cancelled) return;
        setRemaining(Math.max(0, Math.min(100, value)));
      })
      .catch(() => {
        if (!cancelled) setRemaining(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => setCurrency(loadCurrency()), []);

  if (
    remaining === null ||
    remaining <= 0 ||
    hiddenPathFragments.some((fragment) => location.pathname.includes(fragment))
  ) {
    return null;
  }

  const text = copy[lang];
  const foundingPrice = CURRENCIES[currency].foundingPriceFormatted;
  const pricingUrl = `${lp}/pricing?utm_source=site&utm_medium=banner&utm_campaign=founding_100_urgency`;

  return (
    <div className="border-b border-rec/25 bg-ink text-paper dark:border-rec/40 dark:bg-paper dark:text-ink">
      <div className="mx-auto flex max-w-6xl flex-col gap-2 px-6 py-2.5 text-[13px] sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-2 rounded-full border border-paper/20 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-rec dark:border-ink/15">
            <span className="h-1.5 w-1.5 rounded-full bg-rec" aria-hidden="true" />
            {text.label}
          </span>
          <span className="font-semibold">{text.text(remaining)}</span>
          <span className="text-paper/70 dark:text-ink/60">{text.note(foundingPrice)}</span>
        </div>
        <Link
          to={pricingUrl}
          onClick={() => {
            trackGoal("founding100_banner_click", {
              lang,
              source_page: sourcePageFromPath(location.pathname),
              spots_remaining: remaining,
            });
          }}
          className="inline-flex w-fit items-center gap-2 rounded-full bg-paper px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-paper/90 dark:bg-ink dark:text-paper dark:hover:bg-ink/90"
        >
          {text.cta}
          <span aria-hidden="true">-&gt;</span>
        </Link>
      </div>
    </div>
  );
}
